import { useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";

import type { AvtApplyMode, AvtMappingResult } from "@thalamos/aspire-common";
import { mergeFieldText } from "@thalamos/aspire-common";

import { Button } from "../index.js";
import { TextField } from "../TextField/TextField.js";

export type TortusDictationReviewProps = {
  /** Output of `mapSectionsToFields`. */
  result: AvtMappingResult;
  /** Field key → human label for display. */
  fieldLabels: Record<string, string>;
  /** Current form values, used to preview the append/replace merge. */
  currentValues: Record<string, string>;
  /** Called with the field → new-value updates the clinician accepted. */
  onApply: (updates: Record<string, string>) => void;
  onCancel: () => void;
};

type FieldChoice = { included: boolean; mode: AvtApplyMode; text: string };

const buildInitialChoices = (
  result: AvtMappingResult,
  currentValues: Record<string, string>,
): Record<string, FieldChoice> =>
  Object.fromEntries(
    result.fills.map((fill) => [
      fill.field,
      {
        included: true,
        mode:
          (currentValues[fill.field] ?? "").trim().length > 0
            ? "append"
            : "replace",
        text: fill.text,
      } satisfies FieldChoice,
    ]),
  );

/**
 * Review screen for a form-level dictation: shows what `mapSectionsToFields`
 * proposes for each field, lets the clinician edit / include / append-or-replace
 * each one, and emits the accepted field → value updates. Nothing is written
 * until Apply. Sections with no field and fields with no dictation are surfaced
 * so nothing is silently lost.
 */
export function TortusDictationReview({
  result,
  fieldLabels,
  currentValues,
  onApply,
  onCancel,
}: TortusDictationReviewProps) {
  const [choices, setChoices] = useState<Record<string, FieldChoice>>(() =>
    buildInitialChoices(result, currentValues),
  );

  const updateChoice = (field: string, patch: Partial<FieldChoice>) => {
    setChoices((prev) => {
      const existing = prev[field];
      if (existing === undefined) {
        return prev;
      }
      return { ...prev, [field]: { ...existing, ...patch } };
    });
  };

  const handleApply = () => {
    const updates: Record<string, string> = {};
    for (const fill of result.fills) {
      const choice = choices[fill.field];
      if (
        choice === undefined ||
        !choice.included ||
        choice.text.trim().length === 0
      ) {
        continue;
      }
      updates[fill.field] = mergeFieldText(
        currentValues[fill.field] ?? "",
        choice.text,
        choice.mode,
      );
    }
    onApply(updates);
  };

  const unfilledLabels = result.unfilledFields.map(
    (field) => fieldLabels[field] ?? field,
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h6">Review dictation</Typography>
      <Typography variant="body2" color="text.secondary">
        Check what will go into each field, edit as needed, then apply. Nothing
        is saved until you click Apply.
      </Typography>

      {result.fills.map((fill) => {
        const choice = choices[fill.field];
        if (choice === undefined) {
          return null;
        }
        const label = fieldLabels[fill.field] ?? fill.field;
        const hasCurrent = (currentValues[fill.field] ?? "").trim().length > 0;
        return (
          <Box
            key={fill.field}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={choice.included}
                  onChange={(event) => {
                    updateChoice(fill.field, {
                      included: event.target.checked,
                    });
                  }}
                />
              }
              label={<Typography sx={{ fontWeight: 600 }}>{label}</Typography>}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              From: {fill.sources.join(", ")}
            </Typography>

            {choice.included && (
              <>
                {hasCurrent && (
                  <RadioGroup
                    row
                    value={choice.mode}
                    onChange={(event) => {
                      updateChoice(fill.field, {
                        mode: event.target.value as AvtApplyMode,
                      });
                    }}
                  >
                    <FormControlLabel
                      value="append"
                      control={<Radio size="small" />}
                      label="Append to existing"
                    />
                    <FormControlLabel
                      value="replace"
                      control={<Radio size="small" />}
                      label="Replace"
                    />
                  </RadioGroup>
                )}
                <TextField
                  name={`avt-${fill.field}`}
                  label=""
                  value={choice.text}
                  multiline
                  rows="6"
                  useFullWidth
                  onChange={(value: string) => {
                    updateChoice(fill.field, { text: value });
                  }}
                />
              </>
            )}
          </Box>
        );
      })}

      {result.unmatchedSections.length > 0 && (
        <Box>
          <Typography variant="subtitle2">
            Not placed ({result.unmatchedSections.length})
          </Typography>
          <Typography variant="caption" color="text.secondary">
            These dictated sections had no matching field — copy anything useful
            across manually.
          </Typography>
          {result.unmatchedSections.map((section) => (
            <Box key={section.title} sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {section.title}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {section.text}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {unfilledLabels.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          No dictation for: {unfilledLabels.join(", ")}
        </Typography>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button
          label="Cancel"
          variant="outlined"
          color="inherit"
          onClick={onCancel}
        />
        <Button label="Apply" onClick={handleApply} />
      </Box>
    </Box>
  );
}
